/**
 * CreatorBriefsListTab
 *
 * The creator's production queue: every CAPTURING/EDITING brief for the
 * active client (DD-024). Rendered as a WorkspacePanel with PanelList
 * rows (DD-002/TD-004) so the portal shares the admin design language.
 * Rows navigate to the brief detail route.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTrailNavOptions } from "@/core/navigation/trail";
import { useLocale } from "@/hooks/useLocale";
import { usePortalBriefsQuery } from "@/domains/Briefs/hooks/useBriefs";
import { BriefStatusBadge } from "@/domains/Briefs/components/BriefStatusBadge";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelList } from "@/components/ui/PanelList";
import { ENTITY_CONFIG } from "@/core/entityConfig";

interface CreatorBriefsListTabProps {
  clientId: string;
}

export const CreatorBriefsListTab: React.FC<CreatorBriefsListTabProps> = ({
  clientId,
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { data: briefs, isLoading, error } = usePortalBriefsQuery(clientId);
  const [collapsed, setCollapsed] = useState(false);
  // Stamp drill-downs so the detail page publishes a header breadcrumb.
  const trailNav = useTrailNavOptions(t("creator.tabs.brief"));

  const BriefIcon = ENTITY_CONFIG.brief.icon;
  const count = briefs?.length ?? 0;

  return (
    <WorkspacePanel
      title={t("creator.tabs.brief")}
      icon={<BriefIcon className="w-4 h-4" />}
      summary={count > 0 ? count : undefined}
      collapsed={collapsed}
      onToggleCollapse={() => setCollapsed((c) => !c)}
      loading={isLoading}
    >
      {error ? (
        <div className="py-3 text-sm text-red-600">
          {t("creator.briefs.loadError")}
        </div>
      ) : (
        <PanelList
          items={briefs ?? []}
          keyFor={(brief) => brief.id}
          rowClassName="min-h-16 flex flex-col justify-center"
          empty={
            <div className="py-6 text-center text-sm text-gray-400">
              {t("creator.briefs.empty")}
            </div>
          }
          renderItem={(brief) => (
            <button
              type="button"
              onClick={() =>
                navigate(`/creator/${clientId}/briefs/${brief.id}`, trailNav)
              }
              className="w-full flex items-center justify-between gap-3 text-left group"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">
                  {brief.title || t("creator.brief.untitled")}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {brief.ideaCount}{" "}
                  {brief.ideaCount === 1
                    ? t("creator.briefs.idea")
                    : t("creator.briefs.ideas")}
                </div>
              </div>
              <BriefStatusBadge status={brief.status} />
            </button>
          )}
        />
      )}
    </WorkspacePanel>
  );
};
